import { openModal, closeModal } from '@bto-services/modal';

/**
 * Print service for printing vue dynamic components in iframe
 */
export class PrintIframeService {
  /**
   * To print we need to call these functions in following order:
   * 1. openNewFrame() - called from constructor you don't need to call it if you create new service per print
   * 2. addComponentStyle(cssText) - (optional) adds css styles to head of iframe
   * 3. printComponent(component) - (needed) you need to call this with vue component in order to start the print
   * 4. closePrint() - (optional) -  is called automatically after end of print you need to call it if didn't call printComponent
   */
  constructor() {
    this.newFrame = null;
    this.frameLoaded = false;
    this.frameStyles = [];
    this.openNewFrame();
  }
  /**
   * @private
   * After image load event
   */
  imageOnLoadHandler() {
    this.afterPrintEvent();
    this.startPrint();
  }
  /**
   * @private
   * Attach onload events to the iframe
   * @param {()=>void} cb callback function to call on frame load
   */
  attachOnLoadEvents(cb) {
    this.newFrame.onload = cb;
  }
  /**
   * @private
   * Set loading modal while we wait for api or data mapping
   */
  setLoadingMessage() {
    openModal({
      title: '',
      modalClass: 'bto-sb-modalpopup-md',
      message: 'W.load',
    });
  }
  /**
   * @private
   * Close loading modal
   */
  closeLoadingMessage() {
    closeModal();
  }
  /**
   * @private
   * Append frame to document
   */
  appendFrame() {
    document.body.appendChild(this.newFrame);
  }
  /**
   * @private
   * Initialize new frame
   */
  openNewFrame() {
    // if we have frame destroy it
    if (this.newFrame) {
      this.closePrint();
    }
    this.newFrame = document.createElement('iframe');
    this.newFrame.id = 'print-frame';
    this.newFrame.style.position = 'fixed';
    this.newFrame.style.right = '0';
    this.newFrame.style.bottom = '0';
    this.newFrame.style.width = '0';
    this.newFrame.style.height = '0';
    this.newFrame.style.border = '0';
    // set callback to frame load to show whether frame is loaded into document
    const onFrameLoadCb = () => { this.frameLoaded = true; };
    this.attachOnLoadEvents(onFrameLoadCb);
    this.setLoadingMessage();
    this.appendFrame();
  }
  /**
   * @private
   * Open print window or error message if native print is not supported
   */
  startPrint() {
    this.newFrame.contentWindow.focus();
    try {
      this.newFrame.contentWindow.print();
    }
    catch (err) {
      openModal({
        title: '',
        modalClass: 'bto-sb-modalpopup-md',
        message: 'printNotSupported'
      });
    }
  }
  /**
   * @private
   * Reset values to default
   */
  reset() {
    this.newFrame = null;
    this.frameLoaded = false;
    this.frameStyles = [];
  }
  /**
   * @private
   * clean state after print
   * for desktop print call is synchronous so we imediatly call closePrint
   * mobile is asynchronous so we need to wait to call closePrint or the native print will print empty page
   */
  afterPrintEvent() {
    if (window.innerWidth >= 992) {
      this.newFrame.contentWindow.onafterprint = () => {
        this.closePrint();
      };
    } else {
      // on mobile devices initiate close print with timeout, since printing is async
      this.closeLoadingMessage();
      setTimeout(() => {
        this.closePrint();
      }, 60000);
    }
  }
  /**
   * @private
   * Write styles and component into frame
   * @param component vue component
   */
  writeFrameContent(component) {
    this.frameStyles.forEach(style => {
      this.newFrame.contentWindow.document.head.appendChild(style);
    });
    component.$mount();
    this.newFrame.contentWindow.document.body.innerHTML = '';
    this.newFrame.contentWindow.document.body.appendChild(component.$el);
    let img = this.newFrame.contentWindow.document.body.getElementsByTagName('img')[0];
    if (!img) {
      this.imageOnLoadHandler();
      return;
    }
    img.onload = () => { this.imageOnLoadHandler(); };
    img.onerror = () => { this.imageOnLoadHandler(); };
  }
  /**
  * @public
  * Destroy print iframe, hide modal, reset to default state
  */
  closePrint() {
    if (this.newFrame) {
      document.body.removeChild(this.newFrame);
    }
    this.closeLoadingMessage();
    this.reset();
  }
  /**
   * @public
   * Add one or multiple styles in head of iframe
   * @param {string} cssText 
   */
  addComponentStyle(cssText) {
    let componentStyle = document.createElement('style');
    componentStyle.type = 'text/css';
    componentStyle.innerHTML = cssText;
    this.frameStyles.push(componentStyle);
  }
  /**
   * @public
   * Print vue component
   * @param component vue component
   */
  printComponent(component) {
    if (this.frameLoaded) {
      this.writeFrameContent(component);
      return;
    }
    const writeFrameContentCb = () => { this.writeFrameContent(component); };
    this.attachOnLoadEvents(writeFrameContentCb);
  }
  /**
   * @public
   * Mock print used for testing and debugging of vue component
   * @param component 
   */
  mockPrint(component) {
    let newWindow = window.open();
    this.frameStyles.forEach(style => {
      newWindow.document.head.appendChild(style);
    });
    component.$mount();
    newWindow.document.body.innerHTML = '';
    newWindow.document.body.append(component.$el);
    this.closePrint();
  }
}